class CustomProductInformation {
    constructor(section) {
        this.section = section;
        this.mode = "onetime";
        this.currentSellingPlan = null;
        this.init();
    }

    init() {
        this.cacheElements();
        this.initializeSubscription();
        this.bindEvents();
        this.updateSellingPlanInput();
        this.updatePrice();
        this.updateTrustBadges();
    }

    cacheElements() {
        this.form = this.section.querySelector("#custom-product-form");
        this.qtyInput = this.section.querySelector("#qty");
        this.minusBtn = this.section.querySelector(".minus-input");
        this.plusBtn = this.section.querySelector(".plus-input");
        this.variantInput = this.section.querySelector("#variantID");
        this.atcPrice = this.section.querySelector(".product-info-atc-price");
        this.oneTimeBtn = this.section.querySelector(".purchase-options-btn--onetime");
        this.subscribeBtn = this.section.querySelector(".purchase-options-btn--subscribe");
        this.subscriptionWrapper = this.section.querySelector(".js-subscription-wrapper");
        this.frequencyList = this.section.querySelector(".js-options-detail-lists");
        this.trustItems = Array.from(this.section.querySelectorAll(".product-info-trust-item"));
        this.widget = this.section.querySelector(".purchase-widget");
        this.atcbtn = this.form?.querySelector("#addToCartButton");
        this.addToCartText = this.form?.querySelector("#add-to-cart-text");
        this.oneTimePrice = Number(this.widget?.dataset.onetimePrice || 0);
        this.sellingPlanInput = this.form?.querySelector('input[name="selling_plan"]');

        if (this.form && !this.sellingPlanInput) {
            this.sellingPlanInput = document.createElement("input");
            this.sellingPlanInput.type = "hidden";
            this.sellingPlanInput.name = "selling_plan";
            this.form.appendChild(this.sellingPlanInput);
        }
    }

    initializeSubscription() {
        if (!this.subscriptionWrapper) {
            this.mode = "onetime";
            return;
        }

        const selectedPlan = this.frequencyList?.querySelector(".option-detail.is-selected");
        if (selectedPlan) {
            this.currentSellingPlan = Number(selectedPlan.dataset.planId) || null;
        }

        this.mode = "onetime";                              
        this.oneTimeBtn?.classList.add("is-active");      
        this.subscriptionWrapper.classList.remove("is-subscribe"); 
    }

    bindEvents() {
        if (!this.form || !this.atcbtn || !this.qtyInput) return;

        const changeQuantity = delta => {
            const qty = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + delta);
            this.qtyInput.value = qty;
            this.updatePrice();
        };

        this.minusBtn?.addEventListener("click", () => changeQuantity(-1));
        this.plusBtn?.addEventListener("click", () => changeQuantity(1));

        const updateMode = mode => {
            this.mode = mode;
            if (mode === "subscribe") {
                this.subscribeBtn?.classList.add("is-active");
                this.oneTimeBtn?.classList.remove("is-active");
                this.subscriptionWrapper?.classList.add("is-subscribe");
            } else {
                this.oneTimeBtn?.classList.add("is-active");
                this.subscribeBtn?.classList.remove("is-active");
                this.subscriptionWrapper?.classList.remove("is-subscribe");
            }
            this.updateSellingPlanInput();
            this.updatePrice();
            this.updateTrustBadges();
        };

        this.oneTimeBtn?.addEventListener("click", () => updateMode("onetime"));
        this.subscribeBtn?.addEventListener("click", () => updateMode("subscribe"));

        this.frequencyList?.addEventListener("click", event => {
            const option = event.target.closest(".option-detail");
            if (!option) return;

            this.frequencyList
                .querySelectorAll(".option-detail")
                .forEach(item => item.classList.remove("is-selected"));

            option.classList.add("is-selected");
            this.currentSellingPlan = Number(option.dataset.planId) || null;
            this.updateSellingPlanInput();
            this.updatePrice();
        });

        this.atcbtn.addEventListener("click", event => {
            event.preventDefault();
            this.updateSellingPlanInput();
            this.addToCart(new FormData(this.form));
        });
    }

    updateSellingPlanInput() {
        if (!this.sellingPlanInput) return;
        this.sellingPlanInput.value = this.mode === "subscribe" && this.currentSellingPlan
            ? String(this.currentSellingPlan)
            : "";
    }

    updatePrice() {
        if (!this.atcPrice || !this.qtyInput) return;

        const qty = parseInt(this.qtyInput.value, 10) || 1;
        let price = this.oneTimePrice;

        if (this.mode === "subscribe") {
            const selected = this.frequencyList?.querySelector(".option-detail.is-selected");
            price = Number(selected?.dataset.price || price);
        }

        this.atcPrice.innerHTML = this.formatMoney(price * qty);
    }

    formatMoney(cents) {
        return new Intl.NumberFormat(
            document.documentElement.lang || "en-US",
            {
                style: "currency",
                currency: (window.Shopify?.currency?.active) || "USD"
            }
        ).format(cents / 100);
    }

    updateTrustBadges() {
        if (!this.trustItems.length || !this.widget) return;

        const svg = '<svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        const {
            ["onetimeBadge-1"]: onetimeBadge1 = "",
            ["onetimeBadge-2"]: onetimeBadge2 = "",
            ["subscriptionBadge-1"]: subscriptionBadge1 = "",
            ["subscriptionBadge-2"]: subscriptionBadge2 = ""
        } = this.widget.dataset;

        const badges = this.mode === "subscribe"
            ? [subscriptionBadge1, subscriptionBadge2]
            : [onetimeBadge1, onetimeBadge2];

        this.trustItems.forEach((item, index) => {
            if (!item) return;
            item.innerHTML = svg + (badges[index] || "");
        });
    }

    addToCart(formData) {
        const item = {
            id: Number(formData.get('id')) || Number(this.variantInput?.value),
            quantity: Number(formData.get('quantity')) || 1,
            selling_plan: formData.get('selling_plan') || undefined
        };

        if (!item.id) return;

        const originalText = this.addToCartText?.textContent;
        if (this.addToCartText) {
            this.addToCartText.textContent = 'Adding...';
        }

        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(item)
        })
            .then(async response => {
                if (!response.ok) throw new Error(await response.text());
                return response.json();
            })
            .then(() => this.OpenCart())
            .catch(() => {})
            .finally(() => {
                if (this.addToCartText && originalText !== undefined) {
                    this.addToCartText.textContent = originalText;
                }
            });
    }

    OpenCart() {
        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                document.querySelectorAll('.header-cart-item-count, .cart-count').forEach(el => {
                    el.textContent = cart.item_count;
                });
            })
            .catch(() => {});

        setTimeout(() => {
            try {
                const bubble = document.getElementById('cart-icon-bubble') || document.querySelector('.cart-icon-bubble');
                if (bubble) {
                    bubble.click();
                    return;
                }

                const drawer = document.querySelector('cart-drawer') || document.querySelector('.slide-cart-wrapper');
                if (drawer) {
                    if (typeof drawer.open === 'function') {
                        drawer.open();
                    } else {
                        drawer.classList.add('active', 'is-open');
                    }
                    return;
                }

                document.querySelectorAll('.cart-icon, [data-cart-icon], a[href="/cart"]').forEach(el => el.click());
                document.dispatchEvent(new CustomEvent('cart:open'));
                document.dispatchEvent(new CustomEvent('open:cart'));
            } catch (e) {
                // ignore
            }
        }, 400);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".custom-product-information").forEach(section => new CustomProductInformation(section));
});